import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";
import { resolveImageUrl } from "@/lib/media";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import { Target } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = {
  content: {
    image: "https://images.unsplash.com/photo-1516841273335-e39b37888115?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    overline_id: "TENTANG KAMI",
    overline_en: "ABOUT US",
    title_id: "Rumah Sakit Terdepan",
    title_en: "A Leading Hospital",
    p1_id: "",
    p1_en: "",
    p2_id: "",
    p2_en: "",
    badge_value: "25+",
    badge_label_id: "Tahun Pengalaman",
    badge_label_en: "Years of Experience",
  },
  pillars: [],
};

const pick = (obj, lang, key) => {
  if (!obj) return "";
  const v = obj[`${key}_${lang}`];
  if (v && String(v).trim()) return v;
  const alt = lang === "id" ? "en" : "id";
  return obj[`${key}_${alt}`] || "";
};

const resolveIcon = (name) => LucideIcons[name] || Target;

const About = () => {
  const { t, lang } = useLanguage();
  const [data, setData] = useState(FALLBACK);

  useEffect(() => {
    axios
      .get(`${API}/about`)
      .then((r) => {
        const c = r?.data?.content;
        const p = Array.isArray(r?.data?.pillars) ? r.data.pillars : [];
        setData({
          content: c && typeof c === "object" ? c : FALLBACK.content,
          pillars: p.length > 0 ? p : FALLBACK.pillars,
        });
      })
      .catch(() => setData(FALLBACK));
  }, []);

  const c = data.content || FALLBACK.content;
  const overline = pick(c, lang, "overline") || t.about.overline;
  const title = pick(c, lang, "title") || t.about.title;
  const p1 = pick(c, lang, "p1") || t.about.p1;
  const p2 = pick(c, lang, "p2") || t.about.p2;
  const badgeLabel =
    pick(c, lang, "badge_label") || t.hero.stats.experience || "Years of Experience";

  return (
    <section
      id="about"
      data-testid="about-section"
      className="section-y bg-white relative"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image side */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-xl shadow-blue-900/10">
              <img
                src={resolveImageUrl(c.image)}
                alt={title}
                className="w-full h-full object-cover"
                data-testid="about-image"
              />
            </div>
            {(c.badge_value || badgeLabel) && (
              <div
                data-testid="about-badge"
                className="hidden md:block absolute -bottom-8 -right-8 bg-blue-800 text-white rounded-3xl p-6 max-w-[240px] shadow-2xl shadow-blue-900/30"
              >
                {c.badge_value && (
                  <div className="font-heading text-4xl font-bold">{c.badge_value}</div>
                )}
                <div className="text-sm text-blue-100 mt-1">{badgeLabel}</div>
              </div>
            )}
          </motion.div>

          {/* Text side */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="overline mb-4" data-testid="about-overline">
              {overline}
            </div>
            <h2
              data-testid="about-title"
              className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6"
            >
              {title}
            </h2>
            {p1 && <p className="text-slate-600 leading-relaxed mb-4" data-testid="about-p1">{p1}</p>}
            {p2 && <p className="text-slate-600 leading-relaxed mb-8" data-testid="about-p2">{p2}</p>}

            <div className="space-y-4" data-testid="about-pillars">
              {(data.pillars.length > 0
                ? data.pillars
                : [
                    { id: "vision", icon: "Compass", label_id: t.about.vision_label, label_en: t.about.vision_label, text_id: t.about.vision, text_en: t.about.vision },
                    { id: "mission", icon: "Target", label_id: t.about.mission_label, label_en: t.about.mission_label, text_id: t.about.mission, text_en: t.about.mission },
                    { id: "values", icon: "Heart", label_id: t.about.values_label, label_en: t.about.values_label, text_id: t.about.values, text_en: t.about.values },
                  ]
              ).map((p) => {
                const Icon = resolveIcon(p.icon);
                const label = lang === "en" ? (p.label_en || p.label_id) : (p.label_id || p.label_en);
                const text = lang === "en" ? (p.text_en || p.text_id) : (p.text_id || p.text_en);
                return (
                  <div
                    key={p.id}
                    data-testid={`about-pillar-${p.id}`}
                    className="flex gap-4 p-4 rounded-2xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50/50 transition-all"
                  >
                    <div className="shrink-0 h-11 w-11 rounded-xl bg-blue-100 text-blue-800 flex items-center justify-center">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-slate-900">
                        {label}
                      </div>
                      <div className="text-sm text-slate-600 mt-1">{text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
